import React, { useState } from "react";
import {
  Image,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Platform,
  Alert,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { firebaseApp } from "../../firebase/config";
import { getAuth, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useDispatch } from "react-redux";
import { AntDesign } from "@expo/vector-icons";
import logo from "../../assets/images/wc-logo.png";
import ScreenImageBackground from "../../components/ScreenImageBackground";
import { authActions } from "../../store/auth-slice";
import styles from "./styles";
import { useRecaptcha } from "../../components/MFA/hooks/useRecaptcha";
import MFAPage from "../../components/MFA/MFAPage";
import { verifyUserMFA } from "../../components/MFA/api";

// SECURITY: Import validation utilities
import { validateLoginInputs } from "../../utils/authValidation";
import {
  getGenericAuthErrorMessage,
  shouldAllowRetry,
} from "../../utils/authErrorHandling";

export default function LoginScreen({ navigation }) {
  const auth = getAuth(firebaseApp);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [displayMFAPage, setDisplayMFAPage] = useState(null);
  const [verificationCodeId, setVerificationCodeId] = useState();
  const [resolver, setResolver] = useState();
  const recaptcha = useRecaptcha("sign-in");
  const dispatch = useDispatch();

  const loginWithGoogle = async () => {
    if (Platform.OS !== "web") {
      Alert.alert(
        "Google Sign-In",
        "Google sign-in is currently only supported in the web browser version of this app. Please sign in using your email and password."
      );
      return;
    }

    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      setLoading(false);
    } catch (error) {
      if (error.code === "auth/multi-factor-auth-required") {
        handleMFA(error);
      } else {
        console.error("Google Sign-In Error:", error);
        alert("Failed to sign in with Google. Please try again.");
        setLoading(false);
      }
    }
  };

  /**
   * SECURITY: Handles login with input validation and generic error messages
   * 🔒 Validates email format and password presence before Firebase call
   * 🔒 Replaces specific error messages to prevent email enumeration
   */
  const onLoginPress = async () => {
    // SECURITY: Validate inputs before authentication attempt
    const validation = validateLoginInputs(email, password);
    if (!validation.isValid) {
      // Show validation errors to guide user
      const errorMessage = validation.errors.join("\n");
      if (Platform.OS === "web") {
        alert(errorMessage);
      } else {
        Alert.alert("Invalid Input", errorMessage);
      }
      return;
    }

    setLoading(true);
    try {
      // SECURITY: Use sanitized inputs (trimmed, lowercase email)
      await signInWithEmailAndPassword(
        auth,
        validation.sanitized.email,
        validation.sanitized.password
      );
      setLoading(false);
    } catch (error) {
      // SECURITY: Handle specific error codes before sending to frontend
      if (error.code === "auth/multi-factor-auth-required") {
        handleMFA(error);
        return;
      }

      // SECURITY: Get generic error message to prevent enumeration
      const { message: errorMessage, isRateLimit } =
        getGenericAuthErrorMessage(error);

      if (Platform.OS === "web") {
        alert(errorMessage);
      } else {
        Alert.alert("Login Failed", errorMessage);
      }

      // SECURITY: Disable retry if rate limited
      if (isRateLimit) {
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  };
  const redirectUserToHomePage = () => {
    dispatch(authActions.authenticate({ isAuth: true }));
  };

  /**
   * SECURITY: MFA handling with generic error messages
   * 🔒 Converts Firebase MFA errors to generic messages
   */
  const handleMFA = async (error) => {
    if (recaptcha) {
      try {
        const data = await verifyUserMFA(error, recaptcha, 0);

        if (!data) {
          if (Platform.OS === "web") {
            alert("MFA setup failed. Please try again.");
          } else {
            Alert.alert(
              "MFA Failed",
              "Something went wrong. Please try again. If the issue persists, please contact support."
            );
          }
        } else {
          const { verificationId, resolver } = data;
          setVerificationCodeId(verificationId);
          setResolver(resolver);
          setDisplayMFAPage("login");
        }
      } catch (mfaError) {
        console.error("[MFA_ERROR]", mfaError);
        if (Platform.OS === "web") {
          alert("MFA verification failed. Please try again.");
        } else {
          Alert.alert("MFA Error", "Failed to verify MFA. Please try again.");
        }
      }
    } else {
      if (Platform.OS === "web") {
        alert("reCAPTCHA not initialized. Please refresh and try again.");
      } else {
        Alert.alert(
          "reCAPTCHA Error",
          "reCAPTCHA not initialized. Please try again."
        );
      }
    }
  };

  

  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flex: 1 }}
      enableOnAndroid={true}
      automaticallyAdjustContentInsets={true}
      enableAutomaticScroll={Platform.OS === "ios"}
    > 
      <View style={styles.container}>
        <ScreenImageBackground>
          <View style={styles.logoContainer}>
            <Image source={logo} style={styles.logo} />
          </View>
          {!!displayMFAPage ? (
            <MFAPage
              type={displayMFAPage}
              redirectUserToHomePage={redirectUserToHomePage}
              resolver={resolver}
              verificationCodeId={verificationCodeId}
            />
          ) : (
            <View style={styles.loginContainer}>
              <View style={styles.loginForm}>
                <Text style={styles.loginTitle}>LOGIN</Text>
                <Text style={styles.loginSubTitle}>Sign in to continue</Text>

                <Text style={styles.inputLabel}>PLEASE ENTER YOUR EMAIL</Text>
                <TextInput
                  style={styles.textInput}
                  onChangeText={(text) => setEmail(text)}
                  value={email}
                  autoCapitalize="none"
                />

                <Text style={styles.inputLabel}>
                  PLEASE ENTER YOUR PASSWORD
                </Text>
                <TextInput
                  style={styles.textInput}
                  secureTextEntry={true}
                  onChangeText={(text) => setPassword(text)}
                  value={password}
                  autoCapitalize="none"
                />

                <TouchableOpacity
                  style={styles.loginButton}
                  onPress={() => onLoginPress()}
                >
                  <Text style={styles.loginText}>Login</Text>
                  <AntDesign name="arrowright" size={24} color="white" />
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.loginButton, { backgroundColor: "#4285F4", marginTop: -10, marginBottom: 20 }]}
                  onPress={() => loginWithGoogle()}
                >
                  <Text style={styles.loginText}>Sign In with Google</Text>
                  <AntDesign name="google" size={24} color="white" />
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => navigation.navigate("ResetPassword")}
                >
                  <Text style={styles.inputLabel}>FORGOT PASSWORD?</Text>
                </TouchableOpacity>
                {Platform.OS === 'web' ? <div id="sign-in" /> : <View id='sign-in' />}
              </View>
            </View>
          )}
        </ScreenImageBackground>
      </View>
    </KeyboardAwareScrollView>
  );
}
